// src/components/assistant/AssistantSidebar.tsx
//
// Sidebar del shell tipo ChatGPT: Nuevo chat, secciones (Librería, Agentes),
// historial de conversaciones, y menú de usuario (Personalización, volver al
// dashboard, cerrar sesión). Todo con tokens semánticos (light/dark).

import { useState } from 'react';
import {
  SquarePen,
  Library,
  Bot,
  Settings,
  LayoutGrid,
  LogOut,
  MessageSquare,
  PanelLeftClose,
} from 'lucide-react';
import { ThreadListRow } from './ThreadTitle';

export type AssistantView = 'chat' | 'library' | 'agents';

export interface ThreadListItem {
  id: string;
  title: string;
  /** true si el hilo tiene nombre (manual o automático); false = vista previa. */
  hasTitle?: boolean;
  at?: string | null;
}

interface AssistantSidebarProps {
  orgName?: string;
  orgLogo?: string | null;
  view: AssistantView;
  activeThreadId: string | null;
  threads: ThreadListItem[];
  loadingThreads: boolean;
  onNewChat: () => void;
  onSelectView: (v: AssistantView) => void;
  onSelectThread: (id: string) => void;
  /** Renombrar en línea (doble clic o lápiz). Sin esto, la lista es de solo lectura. */
  onRenameThread?: (id: string, title: string) => Promise<void> | void;
  onOpenPersonalization: () => void;
  onBackToDashboard: () => void;
  onLogout: () => void;
  onCollapse?: () => void;
  /** El historial vive en su propio panel (ThreadHistory): aquí solo el menú. */
  hideHistory?: boolean;
}

function NavButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
        active ? 'bg-muted font-medium text-foreground' : 'text-foreground/80 hover:bg-muted/60'
      }`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

export function AssistantSidebar({
  orgName,
  orgLogo,
  view,
  activeThreadId,
  threads,
  loadingThreads,
  onNewChat,
  onSelectView,
  onSelectThread,
  onRenameThread,
  onOpenPersonalization,
  onBackToDashboard,
  onLogout,
  onCollapse,
  hideHistory = false,
}: AssistantSidebarProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <aside className="flex h-full w-full flex-col overscroll-contain border-r border-border bg-card pb-[env(safe-area-inset-bottom)]">
      {/* Brand + colapsar */}
      <div className="flex items-center justify-between px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {orgLogo ? (
            <img src={orgLogo} alt="" className="h-6 w-6 rounded" />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary/10">
              <Bot size={14} className="text-primary" />
            </div>
          )}
          <span className="truncate text-sm font-semibold">{orgName || 'Asistente'}</span>
        </div>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Ocultar panel"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      {/* Nuevo chat */}
      <div className="px-3">
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center gap-2.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <SquarePen size={16} />
          Nuevo chat
        </button>
      </div>

      {/* Secciones */}
      <nav className="mt-2 space-y-0.5 px-3">
        <NavButton
          icon={<MessageSquare size={16} />}
          label="Chat"
          active={view === 'chat'}
          onClick={() => onSelectView('chat')}
        />
        <NavButton
          icon={<Library size={16} />}
          label="Librería"
          active={view === 'library'}
          onClick={() => onSelectView('library')}
        />
        <NavButton
          icon={<Bot size={16} />}
          label="Agentes"
          active={view === 'agents'}
          onClick={() => onSelectView('agents')}
        />
      </nav>

      {hideHistory && <div className="flex-1" />}

      {/* Historial */}
      <div className={`mt-4 min-h-0 flex-1 flex-col px-3 ${hideHistory ? 'hidden' : 'flex'}`}>
        <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Historial
        </p>
        <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
          {loadingThreads && <p className="px-3 py-2 text-xs text-muted-foreground">Cargando…</p>}
          {!loadingThreads && threads.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Sin conversaciones aún.</p>
          )}
          {threads.map((t) => (
            <ThreadListRow
              key={t.id}
              title={t.title}
              active={view === 'chat' && activeThreadId === t.id}
              onSelect={() => onSelectThread(t.id)}
              onRename={onRenameThread ? (title) => onRenameThread(t.id, title) : undefined}
            />
          ))}
        </div>
      </div>

      {/* Menú de usuario */}
      <div className="relative border-t border-border p-3">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground/80 transition-colors hover:bg-muted"
        >
          <Settings size={16} />
          Ajustes
        </button>
        {menuOpen && (
          <div className="absolute bottom-14 left-3 right-3 z-10 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onOpenPersonalization();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <Settings size={15} /> Personalización
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onBackToDashboard();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <LayoutGrid size={15} /> Ir al dashboard
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onLogout();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-destructive hover:bg-muted"
            >
              <LogOut size={15} /> Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default AssistantSidebar;
